/**
 * Money / Cent-Conversion Utilities
 *
 * DISCIPLINE: All Euro amounts stored in the `payments` Firestore collection
 * MUST be integers (cents).  Float arithmetic on currency values accumulates
 * rounding errors — never store a float in the `payments` collection.
 *
 * Conversion boundary: convert to cents ONCE at the UI input boundary
 * (via eurosToCents) and back to a display value ONCE at the render boundary
 * (via centsToEuros).  All internal computation and persistence happens in cents.
 *
 * @module moneyUtils
 */

/**
 * Converts a Euro amount (float) to an integer cent value.
 *
 * Uses `Math.round` to eliminate floating-point rounding artifacts, e.g.
 * `0.1 + 0.2 = 0.30000000000000004` → `round(0.30000000000000004 * 100) = 30`.
 *
 * @param euros - Euro amount (e.g. `9.99`).  Must be finite.
 * @returns Integer cent value (e.g. `999`).
 * @throws {Error} If the input is not a finite number.
 *
 * @example
 * eurosToCents(9.99)   // 999
 * eurosToCents(0.1 + 0.2) // 30  (not 30.000…4)
 *
 * NIE Float in der payments-Collection speichern — nur den Rückgabewert dieser Funktion.
 */
export function eurosToCents(euros: number): number {
  if (!Number.isFinite(euros)) {
    throw new Error(`eurosToCents: expected finite number, got ${euros}`);
  }
  return Math.round(euros * 100);
}

/**
 * Converts an integer cent value back to a Euro amount (float).
 *
 * Use only at the display boundary (render / format).  Do NOT use the result
 * in further arithmetic before converting back to cents.
 *
 * @param cents - Integer cent value (e.g. `999`).  Must be a finite integer.
 * @returns Euro amount (e.g. `9.99`).
 * @throws {Error} If the input is not a finite integer.
 *
 * @example
 * centsToEuros(999)  // 9.99
 * centsToEuros(100)  // 1
 */
export function centsToEuros(cents: number): number {
  if (!Number.isFinite(cents) || !Number.isInteger(cents)) {
    throw new Error(`centsToEuros: expected finite integer, got ${cents}`);
  }
  return cents / 100;
}
